import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@core/Store'
import {
  StructureSearchGroupHeader,
  StructureSearchGroupSubHeader,
} from '../../StructuredSearch.styles'
import {
  ButtonGroupItemStyled,
  ButtonGroupStyled,
} from '@core/Components/Buttons/ButtonGroup.styles'
import { OverlayOpen } from '@core/Components/Overlay'
import { StructuredSearchTrackModal, IconTypes } from './StructuredSearchLocationTrackModal'
import { Chip } from '@core/Areas/StructuredSearch/Components/StructuredSearchLocation/Components/Chips/Chips'
import { ChipContainerStyled } from '@core/Areas/StructuredSearch/Components/StructuredSearchLocation/Components/Chips/Chips.styles'
import { getFilteredTracks } from './track-utils'
import { CloseChipIcon, AddChipIcon, ChipWithRightIcon } from './StructuredSearchLocation.styles'
import { colors } from '@core/Settings'
import { setSearchField, setSelectedTracks, StructuredSearchFields } from '../../Store'

export const StructuredSearchLocation = (): JSX.Element => {
  const dispatch = useDispatch()

  const {
    searchCriteria: {
      locationCountryAustralia: aus,
      locationCountryNewZealand: nz,
      locationCountryInternational: int,
      selectedTracks,
    },
    searchResults: { aggregations },
  } = useSelector((state: RootState) => state.structuredSearch)

  const onChangeCallback = (
    field: keyof StructuredSearchFields,
    value: StructuredSearchFields[typeof field]
  ) => {
    dispatch(setSearchField({ field, value }))
  }

  const addTrack = () => {
    OverlayOpen(StructuredSearchTrackModal)
  }

  const removeTrack = (id: string) => {
    if (selectedTracks) {
      const newSelectedTracks = selectedTracks.filter(track => track !== id)
      dispatch(setSelectedTracks(newSelectedTracks))
    }
  }

  const availableTracks = aggregations?.tracks || []
  const displayableTracks = getFilteredTracks(availableTracks, selectedTracks)

  const mappedChips = displayableTracks?.map(track => {
    const iconType = IconTypes[track.contestType as keyof typeof IconTypes]
    return (
      <ChipWithRightIcon
        onClick={() => removeTrack(track.id)}
        color='filled'
        key={track.id}
        data-tid-structured-search-location-chip={track.id}
      >
        {track.name}
        {!track.isAustralian && <> ({track.location})</>}

        {track.isDuplicate && (
          <CloseChipIcon
            size='2rem'
            type={iconType}
            color={colors.structuredSearchLocationModal.icon.code}
          />
        )}

        <CloseChipIcon type='remove' size='1.1rem' />
      </ChipWithRightIcon>
    )
  })

  return (
    <>
      <StructureSearchGroupHeader>Location</StructureSearchGroupHeader>
      <ButtonGroupStyled>
        <ButtonGroupItemStyled
          id='location-country-australia'
          data-tid-location-country='Australia'
          selected={aus}
          onClick={() => onChangeCallback('locationCountryAustralia', !aus)}
        >
          Australia
        </ButtonGroupItemStyled>
        <ButtonGroupItemStyled
          id='location-country-new-zealand'
          data-tid-location-country='New Zealand'
          selected={nz}
          onClick={() => onChangeCallback('locationCountryNewZealand', !nz)}
        >
          New Zealand
        </ButtonGroupItemStyled>
        <ButtonGroupItemStyled
          id='location-country-international'
          data-tid-location-country='International'
          selected={int}
          onClick={() => onChangeCallback('locationCountryInternational', !int)}
        >
          International
        </ButtonGroupItemStyled>
      </ButtonGroupStyled>

      <StructureSearchGroupSubHeader>Track</StructureSearchGroupSubHeader>

      <ChipContainerStyled>
        {mappedChips}
        <Chip
          onClick={addTrack}
          color='hollow'
          data-tid-structured-search-location-chip='add-track'
        >
          <AddChipIcon type='add' size='1.2rem' /> Add a Track
        </Chip>
      </ChipContainerStyled>
    </>
  )
}
